/**
 * <delivery-countdown> — live same-day delivery cut-off countdown.
 *
 * Computes the time remaining until the same-day cut-off in the store's
 * timezone (Europe/Dublin by default) and renders it into the same-day line,
 * ticking every second. A day has up to three phases, each matching what the
 * cart accepts for today:
 *   a. Standard same-day, until the county cut-off           (data-template)
 *   b. Dublin's paid Express / Evening, until the late cut-off (data-late-template)
 *   c. The merchant's after-cut-off message                    (data-after)
 *
 * Markup contract (rendered by blocks/delivery-countdown.liquid):
 *   <delivery-countdown
 *     data-cutoff-hour="14" data-cutoff-minute="0"
 *     data-timezone="Europe/Dublin"
 *     data-same-day-days="1,2,3,4,5,6,0"
 *     data-county="dublin"
 *     data-cutoff-rules='{"mon":"1400",…}'      ← delivery rules mode only
 *     data-offdays / data-global-offdays / data-standard-offdays='["20261225"]'
 *     data-next-day-only="true"                 ← county never gets same-day
 *     data-late-cutoff-hour="16"                ← phase b enabled
 *     data-express-offdays / data-evening-offdays='[…]'>
 *     <p class="delivery-countdown__line--sameday"
 *        data-template="Order within {time} for same-day delivery in Dublin"
 *        data-after="Order today for next-day delivery in Dublin"
 *        data-late-template="Order within {time} for same-day {services} delivery in Dublin"></p>
 *   </delivery-countdown>
 *
 * {time} is replaced with a highlighted span, e.g. "2h 15m"; {services} with
 * "Express or Evening" (or whichever one runs today).
 */
const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

class DeliveryCountdown extends HTMLElement {
  connectedCallback() {
    this.cutoffHour = parseInt(this.dataset.cutoffHour, 10);
    this.cutoffMinute = parseInt(this.dataset.cutoffMinute || '0', 10);
    this.timezone = this.dataset.timezone || 'Europe/Dublin';
    this.sameDayDays = (this.dataset.sameDayDays || '1,2,3,4,5,6,0')
      .split(',')
      .map((d) => parseInt(d.trim(), 10))
      .filter((d) => !Number.isNaN(d));

    // Per-weekday cut-offs straight from the deldate-rules metafield the cart
    // uses (data-cutoff-rules), so the banner tracks ops changes instead of a
    // hardcoded hour. Picked client-side because the page is cached and a
    // server-picked weekday goes stale at midnight. Values may be strings.
    this.county = this.dataset.county || '';
    this.cutoffRules = this.parseJson(this.dataset.cutoffRules);
    this.closedDates = [...this.dateList(this.dataset.globalOffdays), ...this.dateList(this.dataset.offdays)];
    this.standardOffdays = this.dateList(this.dataset.standardOffdays);
    this.nextDayOnly = this.dataset.nextDayOnly === 'true';

    // Phase b: the paid late window and the dates each service is switched off.
    this.lateCutoffHour = parseInt(this.dataset.lateCutoffHour, 10);
    this.lateServices = [
      ['Express', this.dateList(this.dataset.expressOffdays)],
      ['Evening', this.dateList(this.dataset.eveningOffdays)],
    ];

    this.line = this.querySelector('.delivery-countdown__line--sameday');
    if ((Number.isNaN(this.cutoffHour) && !this.cutoffRules) || !this.line) return;

    this.template = this.line.dataset.template || 'Order within {time} for same-day delivery';
    this.lateTemplate = this.line.dataset.lateTemplate || '';
    this.afterText = this.line.dataset.after || '';

    this.render();
    // A tab left open overnight (or restored from the back/forward cache) must
    // pick the new day up again, so re-render as soon as it is visible.
    this.onVisibility = () => {
      if (document.visibilityState === 'visible') this.render();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** Current time + weekday in the store timezone. */
  storeNow() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: this.timezone,
      hour12: false,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).formatToParts(new Date());

    const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
    const year = get('year');
    const month = get('month');
    const day = get('day');
    let hour = get('hour');
    if (hour === 24) hour = 0; // some engines emit 24 for midnight
    const minute = get('minute');
    const second = get('second');
    // Weekday (0=Sun..6=Sat) for this calendar date in the store tz.
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    // YYYYMMDD, the same shape deldate-rules uses for off-days.
    const ymd = `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`;
    return { hour, minute, second, weekday, ymd };
  }

  parseJson(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /** An off-day list as YYYYMMDD strings (entries may be numbers; null = none). */
  dateList(raw) {
    const list = this.parseJson(raw);
    return Array.isArray(list) ? list.map(String) : [];
  }

  /** A metafield HHMM cut-off in seconds, or null for closed/malformed ("0", "", "1475"). */
  cutoffSecs(value) {
    const text = String(value ?? '').trim();
    if (!/^\d{3,4}$/.test(text)) return null;
    const hours = Math.floor(Number(text) / 100);
    const minutes = Number(text) % 100;
    if (hours > 23 || minutes > 59 || hours + minutes === 0) return null;
    return hours * 3600 + minutes * 60;
  }

  /**
   * Today's same-day windows, in seconds from midnight (null = not today).
   * With delivery rules present this mirrors the cart's validateDeliveryDate:
   * next-day-only counties, Louth/Meath from nationwide rules on Saturdays,
   * off-days (every county + this one), the weekday cut-off, then Standard's
   * own blackout. Without them it falls back to the block's hour + weekday list.
   */
  todayWindows({ weekday, ymd }) {
    // Option hours come from UFO's Option Cut Off grid (snippets/delivery-rules-switch.liquid).
    // Guarded because the snippet is rendered by the block, not by this module.
    const hours = window.deliveryRulesSwitch && window.deliveryRulesSwitch.optionHours(ymd);
    const optionKeys = { Express: 'express', Evening: 'eve' };
    const closeSecs = (hhmm) => Math.floor(hhmm / 100) * 3600 + (hhmm % 100) * 60;
    const services = this.lateServices
      .filter(([name, off]) => !off.includes(ymd) && (!hours || hours[optionKeys[name]].offered))
      .map(([name]) => name);
    // Only shortens a late window the merchant enabled; never turns one on.
    let late = Number.isNaN(this.lateCutoffHour) ? null : this.lateCutoffHour * 3600;
    if (hours && late !== null && services.length) {
      late = Math.min(late, Math.max(...services.map((name) => closeSecs(hours[optionKeys[name]].close))));
    }
    const lateWindow = late !== null && services.length ? late : null;

    if (!this.cutoffRules) {
      if (!this.sameDayDays.includes(weekday)) return { standard: null, late: null, services };
      return { standard: this.cutoffHour * 3600 + this.cutoffMinute * 60, late: lateWindow, services };
    }

    const saturdayNationwide = weekday === 6 && (this.county === 'louth' || this.county === 'meath');
    const cutoff = this.cutoffSecs(this.cutoffRules[WEEKDAY_KEYS[weekday]]);
    if (this.nextDayOnly || saturdayNationwide || this.closedDates.includes(ymd) || cutoff === null) {
      return { standard: null, late: null, services };
    }
    return { standard: this.standardOffdays.includes(ymd) ? null : cutoff, late: lateWindow, services };
  }

  render() {
    const now = this.storeNow();
    const nowSecs = now.hour * 3600 + now.minute * 60 + now.second;
    const { standard, late, services } = this.todayWindows(now);

    if (standard !== null && nowSecs < standard) {
      this.showCountdown(this.template, standard - nowSecs, '');
    } else if (this.lateTemplate && late !== null && nowSecs < late && (standard === null || late > standard)) {
      this.showCountdown(this.lateTemplate, late - nowSecs, services.join(' or '));
    } else {
      this.line.textContent = this.afterText;
      this.line.classList.add('delivery-countdown__line--after');
      this.line.hidden = this.afterText.trim() === '';
      // Nothing to count down until tomorrow: a slow tick is enough to notice
      // the new day on a tab left open past midnight.
      this.tickEvery(60000);
    }
  }

  showCountdown(template, remaining, services) {
    const hours = Math.floor(remaining / 3600);
    const mins = Math.floor((remaining % 3600) / 60);
    const label = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    this.line.innerHTML = this.escape(template)
      .replace('{services}', this.escape(services))
      .replace('{time}', `<span class="delivery-countdown__time">${label}</span>`);
    this.line.classList.remove('delivery-countdown__line--after');
    this.line.hidden = false;
    // Tick every second so the countdown stays accurate near the cut-off.
    this.tickEvery(1000);
  }

  tickEvery(ms) {
    if (this.timer && this.tickMs === ms) return;
    if (this.timer) clearInterval(this.timer);
    this.tickMs = ms;
    this.timer = setInterval(() => this.render(), ms);
  }

  escape(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('delivery-countdown')) {
  customElements.define('delivery-countdown', DeliveryCountdown);
}
